import {TInventoryMedicine} from '@app/types';
import {TableProps} from 'antd';
import {useSearchParams} from 'react-router-dom';
import {LogEvents, useSendEvent} from '@app/utils/logger';
import {useCallback, useState} from 'react';
import {useDeleteInventoryMedicineMutation} from '@app/redux/services/inventoryService';
import {t} from 'i18next';
import showNotification from '@app/utils/showNotification';

interface IUseInventoryDetailState {
  selectedMedicineId: string;
  selectedInventoryDate: string;
  isDeleteModalOpen: boolean;
  isDetailModalOpen?: boolean;
}
interface IUseInventoryDetailActions {
  onTableChange: TableProps<TInventoryMedicine>['onChange'];
  onDeleteModalOpen: (medicineId: string, inventoryDate: string) => void;
  onDeleteModalClose: () => void;
  onDeleteConfirm: () => void;
  onDetailModalOpen: (medicineId: string, inventoryDate: string) => void;
  onDetailModalClose: () => void;
}

const useInventoryDetail = (): [
  IUseInventoryDetailState,
  IUseInventoryDetailActions,
] => {
  const [state, setState] = useState<IUseInventoryDetailState>({
    selectedMedicineId: '',
    selectedInventoryDate: '',
    isDetailModalOpen: false,
    isDeleteModalOpen: false,
  });
  const sendEvent = useSendEvent();
  const [searchParams, setSearchParams] = useSearchParams();
  const [deleteInventoryMedicine] = useDeleteInventoryMedicineMutation();

  const onTableChange: TableProps<TInventoryMedicine>['onChange'] = (
    pagination,
  ) => {
    const {current, pageSize} = pagination;
    sendEvent(LogEvents.INVENTORY_LIST_TABLE_PAGINATE);
    searchParams.set('page', String(current));
    searchParams.set('limit', String(pageSize));
    setSearchParams(searchParams);
  };
  const onDetailModalOpen = useCallback(
    (medicineId: string, inventoryDate: string) => {
      setState({
        selectedMedicineId: medicineId,
        selectedInventoryDate: inventoryDate,
        isDeleteModalOpen: false,
        isDetailModalOpen: true,
      });
    },
    [],
  );
  const onDetailModalClose = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      selectedInventoryDate: '',
      isDetailModalOpen: false,
      isDeleteModalOpen: false,
    }));
  }, []);
  const onDeleteModalOpen = useCallback(
    (selectedMedicineId: string, inventoryDate: string) => {
      setState({
        selectedMedicineId: selectedMedicineId,
        selectedInventoryDate: inventoryDate,
        isDetailModalOpen: false,
        isDeleteModalOpen: true,
      });
    },
    [],
  );
  const onDeleteModalClose = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      selectedInventoryDate: '',
      isDetailModalOpen: false,
      isDeleteModalOpen: false,
    }));
  }, []);
  const onDeleteConfirm = useCallback(async () => {
    if (!state.selectedInventoryDate) return;

    try {
      sendEvent(LogEvents.INVENTORY_DELETE_PRESS, {
        inventoryDate: state.selectedInventoryDate,
      });
      await deleteInventoryMedicine({
        id: state.selectedMedicineId,
        date: state.selectedInventoryDate,
      }).unwrap();
      showNotification(
        t('Success'),
        t('Successfully deleted!'),
        'bottomRight',
        'success',
      );
    } catch (error) {
      showNotification(
        t('Error'),
        t('Failed to delete inventory. Please try again.'),
        'bottomRight',
        'error',
      );
    } finally {
      onDeleteModalClose();
    }
  }, [
    state.selectedInventoryDate,
    sendEvent,
    deleteInventoryMedicine,
    t,
    onDeleteModalClose,
  ]);
  const actions: IUseInventoryDetailActions = {
    onTableChange,
    onDeleteModalOpen,
    onDeleteModalClose,
    onDeleteConfirm,
    onDetailModalOpen,
    onDetailModalClose,
  };

  return [state, actions];
};

export default useInventoryDetail;
